
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface User {
  id: string;
  full_name: string | null;
  is_premium: boolean;
  premium_since: string | null;
}

interface UsersTableProps {
  users: User[];
  loading: boolean;
  updating: string | null;
  togglePremium: (userId: string, isPremium: boolean) => Promise<void>;
  formatDate: (dateString: string | null) => string;
}

const UsersTable = ({ users, loading, updating, togglePremium, formatDate }: UsersTableProps) => {
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const premiumCount = users.filter(user => user.is_premium).length;

  return (
    <Table>
      <TableCaption>
        {users.length} users displayed | {premiumCount} premium users
      </TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>ID</TableHead>
          <TableHead>Premium</TableHead>
          <TableHead>Premium Since</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.map((user) => (
          <TableRow key={user.id}>
            <TableCell className="font-medium">{user.full_name || "Unknown"}</TableCell>
            <TableCell className="font-mono text-xs">{user.id}</TableCell>
            <TableCell>
              <Badge variant={user.is_premium ? "default" : "outline"}>
                {user.is_premium ? "Premium" : "Free"}
              </Badge>
            </TableCell>
            <TableCell>{formatDate(user.premium_since)}</TableCell>
            <TableCell>
              <Button
                variant="outline"
                size="sm"
                onClick={() => togglePremium(user.id, !user.is_premium)}
                disabled={updating === user.id}
              >
                {updating === user.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  user.is_premium ? "Toggle to Free" : "Toggle to Premium"
                )}
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default UsersTable;
